/**
 * 24K.Infinity — Layout tokens
 * -------------------------------------------------------------------------
 * Spacing, radii, elevation, motion and structural sizes. The reference is
 * restrained: small radii, near-flat elevation (separation by ground
 * contrast + hairline borders, not heavy shadow), and generous whitespace.
 * -------------------------------------------------------------------------
 */

// 4px base rhythm (MUI spacing stays at 8 — these are explicit step tokens).
export const space = {
  0: 0,
  1: 4,
  2: 8,
  3: 12,
  4: 16,
  5: 20,
  6: 24,
  7: 32,
  8: 40,
  9: 48,
  10: 64,
  11: 80,
  12: 96,
};

// Square by design — the Twin Console uses sharp corners everywhere.
// (Genuinely circular elements — avatars, status dots — set '50%' directly
// and are unaffected by these tokens.)
export const radius = {
  xs: 0,
  sm: 0,
  md: 0, // default surface / control — also drives theme.shape.borderRadius
  lg: 0, // cards
  xl: 0, // feature / hero cards
  pill: 0,
};

// Near-flat elevation — soft, low-spread shadows only.
export const shadow = {
  none: 'none',
  xs: '0 1px 2px rgba(16,24,40,0.04)',
  sm: '0 2px 8px rgba(16,24,40,0.06)',
  md: '0 8px 24px rgba(16,24,40,0.08)',
  lg: '0 16px 40px rgba(16,24,40,0.12)',
  // Dark-mode variants lean on lift + faint accent rather than drop shadow.
  darkSm: '0 2px 8px rgba(0,0,0,0.40)',
  darkMd: '0 12px 32px rgba(0,0,0,0.50)',
};

export const focusRing = (color) => `0 0 0 3px ${color}`;

export const motion = {
  duration: { instant: 80, fast: 140, base: 220, slow: 320, slower: 480 },
  easing: {
    standard: 'cubic-bezier(0.2, 0, 0, 1)',
    emphasized: 'cubic-bezier(0.2, 0, 0, 1.0)',
    entrance: 'cubic-bezier(0.0, 0, 0.2, 1)',
    exit: 'cubic-bezier(0.4, 0, 1, 1)',
  },
  // framer-motion presets
  springSoft: { type: 'spring', stiffness: 220, damping: 28 },
  fadeUp: {
    initial: { opacity: 0, y: 12 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.32, ease: [0.2, 0, 0, 1] },
  },
};

// Structural sizes for the app shell.
export const sizing = {
  drawerWidth: 248,
  iconRailWidth: 76,
  topbarHeight: 64,
  maxContentWidth: 1440,
  controlHeight: { sm: 32, md: 40, lg: 48 },
};

export const breakpoints = { xs: 0, sm: 600, md: 900, lg: 1200, xl: 1536 };

export const zIndex = {
  appBar: 1100,
  drawer: 1200,
  modal: 1300,
  snackbar: 1400,
  tooltip: 1500,
};

const layout = { space, radius, shadow, focusRing, motion, sizing, breakpoints, zIndex };
export default layout;
